import { Box, Chip, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import { DocumentStatus } from '../../../../../mocks/data/supplierGovernanceData';
import { RegulatoryComplianceStatus, SupplierEsgProfile } from '../../../../../mocks/data/supplierEsgDashboardData';

export interface GovernanceComplianceTabProps {
  profiles: SupplierEsgProfile[];
}

const DOCUMENT_STATUS_COLOR: Record<DocumentStatus, 'success' | 'warning' | 'error'> = {
  Valid: 'success',
  ExpiringSoon: 'warning',
  Expired: 'error',
};

const REGULATORY_STATUS_COLOR: Record<RegulatoryComplianceStatus, 'success' | 'warning' | 'error'> = {
  Compliant: 'success',
  ActionRequired: 'warning',
  NonCompliant: 'error',
};

const BoolChip = ({ value, trueLabel, falseLabel }: { value: boolean; trueLabel: string; falseLabel: string }) => (
  <Chip label={value ? trueLabel : falseLabel}
    size="small"
    color={value ? 'success' : 'error'}
    variant="outlined" />
);

/** Governance section (certification management, insurance, audit history, contract
 * compliance, code of conduct, anti-bribery, policy acknowledgement, regulatory compliance). */
const GovernanceComplianceTab: React.FC<GovernanceComplianceTabProps> = ({ profiles }) => {
  const { t } = useTranslation('esgDashboard');
  return (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('governance.profileTableTitle', 'Governance profile by supplier')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
                  <TableCell>{t('common.certifications', 'Certifications')}</TableCell>
                  <TableCell align="center">{t('common.insurance', 'Insurance')}</TableCell>
                  <TableCell>{t('governance.lastAudit', 'Last audit')}</TableCell>
                  <TableCell align="right">{t('governance.auditScore', 'Audit score')}</TableCell>
                  <TableCell align="right">{t('governance.contractCompliance', 'Contract compliance')}</TableCell>
                  <TableCell align="center">{t('governance.codeOfConduct', 'Code of Conduct')}</TableCell>
                  <TableCell align="center">{t('governance.antiBribery', 'Anti-bribery')}</TableCell>
                  <TableCell align="right">{t('governance.policyAck', 'Policy ack.')}</TableCell>
                  <TableCell align="center">{t('governance.regulatoryStatus', 'Regulatory status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((p) => (
                  <TableRow key={p.supplierId}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>
                      <Stack direction="row"
                        spacing={0.5}
                        flexWrap="wrap">
                        {p.governanceCertifications.length === 0 ? (
                          <Typography variant="textXS"
                            color="text.secondary">{t('common.none', 'None')}</Typography>
                        ) : p.governanceCertifications.map((cert) => (
                          <Chip key={cert}
                            label={cert}
                            size="small"
                            color="primary"
                            variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={p.insuranceStatus}
                        size="small"
                        color={DOCUMENT_STATUS_COLOR[p.insuranceStatus]}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>{p.lastAuditDate}</TableCell>
                    <TableCell align="right">{p.lastAuditScore}</TableCell>
                    <TableCell align="right">{p.contractComplianceScore}%</TableCell>
                    <TableCell align="center"><BoolChip value={p.codeOfConductAccepted}
                      trueLabel={t('governance.accepted', 'Accepted')}
                      falseLabel={t('governance.notAccepted', 'Not accepted')} /></TableCell>
                    <TableCell align="center"><BoolChip value={p.antiBriberyDeclared}
                      trueLabel={t('common.declared', 'Declared')}
                      falseLabel={t('common.notDeclared', 'Not declared')} /></TableCell>
                    <TableCell align="right">{p.policyAcknowledgementPercentage}%</TableCell>
                    <TableCell align="center">
                      <Chip label={p.regulatoryComplianceStatus}
                        size="small"
                        color={REGULATORY_STATUS_COLOR[p.regulatoryComplianceStatus]}
                        variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>
    </Grid>
  );
};

export default GovernanceComplianceTab;
